import { useState } from "react";
import { useDropzone } from "react-dropzone";
import type { FileRejection } from "react-dropzone";
import {
  Upload,
  X,
  File,
  FileImage,
  FileText,
  AlertCircle,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface FileDropzoneProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  accept?: Record<string, string[]>;
  multiple?: boolean;
  maxSize?: number;
  maxFiles?: number;
  disabled?: boolean;
  className?: string;
  id?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
}

function getFileIcon(file: File) {
  if (file.type.startsWith("image/")) {
    return <FileImage className="h-4 w-4" />;
  }
  if (file.type.startsWith("text/") || file.type.includes("pdf")) {
    return <FileText className="h-4 w-4" />;
  }
  return <File className="h-4 w-4" />;
}

function formatAcceptTypes(accept?: Record<string, string[]>): string {
  if (!accept) return "";
  return Object.entries(accept)
    .flatMap(([, extensions]) => extensions)
    .join(", ");
}

export function FileDropzone({
  files,
  onFilesChange,
  accept,
  multiple = false,
  maxSize,
  maxFiles,
  disabled = false,
  className,
  id,
}: FileDropzoneProps) {
  const [errors, setErrors] = useState<string[]>([]);

  const onDrop = (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    // Clear previous errors
    setErrors([]);

    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const errorMessages: string[] = [];
      rejectedFiles.forEach((rejected) => {
        const { file, errors: fileErrors } = rejected;
        fileErrors.forEach((error) => {
          if (error.code === "file-invalid-type") {
            errorMessages.push(
              `"${file.name}" has an invalid file type. ${accept ? `Accepted types: ${formatAcceptTypes(accept)}` : ""}`,
            );
          } else if (error.code === "file-too-large") {
            errorMessages.push(
              `"${file.name}" is too large. Maximum size: ${maxSize ? formatFileSize(maxSize) : "unknown"}`,
            );
          } else if (error.code === "too-many-files") {
            errorMessages.push(
              `Too many files. Maximum allowed: ${maxFiles || 1}`,
            );
          } else {
            errorMessages.push(`"${file.name}": ${error.message}`);
          }
        });
      });
      // Remove duplicate error messages
      setErrors([...new Set(errorMessages)]);
    }

    // Handle accepted files
    if (acceptedFiles.length === 0) return;

    if (multiple) {
      const newFiles = [...files, ...acceptedFiles];
      const limitedFiles = maxFiles ? newFiles.slice(0, maxFiles) : newFiles;
      onFilesChange(limitedFiles);
    } else {
      onFilesChange([acceptedFiles[0]]);
    }
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop,
      accept,
      maxSize,
      maxFiles: multiple ? maxFiles : 1,
      multiple,
      disabled,
    });

  const handleRemoveFile = (index: number) => {
    const updated = [...files];
    updated.splice(index, 1);
    onFilesChange(updated);
  };

  return (
    <div className={cn("space-y-4", className)}>
      <div
        {...getRootProps()}
        className={cn(
          "relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-input px-6 py-10 text-muted-foreground transition-all outline-none",
          "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
          disabled
            ? "cursor-not-allowed opacity-50"
            : isDragReject
              ? "cursor-not-allowed border-destructive bg-destructive/10 ring-destructive/20"
              : "cursor-pointer hover:text-accent-foreground",
          isDragActive &&
            !isDragReject &&
            "border-ring ring-[3px] ring-ring/50",
        )}
      >
        <input {...getInputProps()} id={id} />
        <Upload className="mb-4 h-10 w-10 opacity-50" />
        <div className="text-center">
          {isDragReject ? (
            <p className="text-sm font-medium text-destructive">
              Invalid file type or size
            </p>
          ) : isDragActive ? (
            <p className="text-sm font-medium">Drop files here...</p>
          ) : (
            <>
              <p className="text-sm font-medium">
                Drop files here or click to browse
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {[
                  accept && `Accepted: ${formatAcceptTypes(accept)}`,
                  maxSize && `Max size: ${formatFileSize(maxSize)}`,
                  maxFiles && multiple && `Max files: ${maxFiles}`,
                ]
                  .filter(Boolean)
                  .join(" • ")}
              </p>
            </>
          )}
        </div>
      </div>

      {errors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-1">
              {errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {files.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">
              Selected file{files.length > 1 ? "s" : ""} ({files.length})
            </p>
            {files.length > 1 && !disabled && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onFilesChange([])}
                className="h-7 text-xs"
              >
                Clear all
              </Button>
            )}
          </div>
          <div className="max-h-80 space-y-2 overflow-y-auto pr-3 [scrollbar-gutter:stable]">
            {files.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center justify-between rounded-md border bg-background p-3"
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  {getFileIcon(file)}
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span
                      className="truncate text-sm font-medium"
                      title={file.name}
                    >
                      {file.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatFileSize(file.size)}
                    </span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile(index);
                  }}
                  disabled={disabled}
                  aria-label="Remove file"
                  className="shrink-0"
                >
                  <X className="size-4" aria-hidden="true" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
